import React, { useEffect, useRef, useState } from "react";
import svgPanZoom from "svg-pan-zoom";

interface ZoomableImageProps {
  url: string;
  alt?: string;
  /** Fixed viewport height (the image pans/zooms inside this box). */
  height?: string;
}

/**
 * A raster image with independent pan & pinch/scroll zoom, using the same
 * `svg-pan-zoom` mechanism as the claim-structure (mermaid) view. The image is
 * embedded in an inline <svg> so svg-pan-zoom can drive it. Each instance is
 * self-contained, so multiple ZoomableImages zoom independently.
 */
const ZoomableImage: React.FC<ZoomableImageProps> = ({
  url,
  alt = "",
  height = "22rem",
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const instanceRef = useRef<ReturnType<typeof svgPanZoom> | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  // Preload to get the natural size so the SVG viewBox matches the image.
  useEffect(() => {
    let active = true;
    setDims(null);
    const img = new Image();
    img.onload = () =>
      active && setDims({ w: img.naturalWidth || 1000, h: img.naturalHeight || 1000 });
    img.onerror = () => active && setDims({ w: 1000, h: 1000 });
    img.src = url;
    return () => {
      active = false;
    };
  }, [url]);

  // Initialise (and re-initialise on image change) the pan/zoom instance.
  useEffect(() => {
    if (!dims || !svgRef.current) return;

    if (instanceRef.current) {
      try {
        instanceRef.current.destroy();
      } catch {
        /* noop */
      }
      instanceRef.current = null;
    }

    instanceRef.current = svgPanZoom(svgRef.current, {
      zoomEnabled: true,
      panEnabled: true,
      controlIconsEnabled: true,
      fit: true,
      center: true,
      minZoom: 1,
      maxZoom: 20,
      zoomScaleSensitivity: 0.3,
    });

    return () => {
      if (instanceRef.current) {
        try {
          instanceRef.current.destroy();
        } catch {
          /* noop */
        }
        instanceRef.current = null;
      }
    };
  }, [dims, url]);

  if (!url) return null;

  return (
    <div className="w-full" style={{ height }}>
      {dims && (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${dims.w} ${dims.h}`}
          width="100%"
          height="100%"
          role="img"
          aria-label={alt}
          style={{ display: "block", touchAction: "none", cursor: "grab" }}
        >
          <image href={url} x={0} y={0} width={dims.w} height={dims.h} />
        </svg>
      )}
    </div>
  );
};

export default ZoomableImage;
